import { ref, watch, onUnmounted } from 'vue';

const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchmove',
  'touchstart',
  'wheel',
];

/**
 * Triggers onIdle callback after the user has been inactive for the given timeout.
 * Activity events tracked: mousemove, mousedown, keydown, touchmove, touchstart, wheel.
 *
 * @param onIdle          - Callback invoked when the idle timeout is reached.
 * @param options.timeout - Inactivity duration in ms before onIdle fires (default: 15 minutes).
 * @param options.active  - Getter that returns whether the timer should be running.
 *                          When it returns false the timer is stopped (e.g. on the login page).
 *                          When omitted the timer always runs.
 */
export function useIdleTimeout(
  onIdle: () => void | Promise<void>,
  options: { timeout?: number; active?: () => boolean } = {},
): void {
  const { timeout = DEFAULT_IDLE_TIMEOUT_MS, active } = options;

  const idle = ref(false);
  let timer: ReturnType<typeof setTimeout> | undefined;

  function reset(): void {
    idle.value = false;
    clearTimeout(timer);
    timer = setTimeout(() => {
      idle.value = true;
    }, timeout);
  }

  function stopListening(): void {
    clearTimeout(timer);
    timer = undefined;
    for (const event of ACTIVITY_EVENTS) {
      document.removeEventListener(event, reset);
    }
  }

  function startListening(): void {
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, reset, { passive: true });
    }
    reset();
  }

  if (active) {
    // Watch the getter reactively so the timer starts/stops with auth state
    watch(active, (isActive) => {
      if (isActive) {
        startListening();
      } else {
        stopListening();
      }
    }, { immediate: true });
  } else {
    startListening();
  }

  onUnmounted(() => {
    stopListening();
  });

  let idleRunning = false;
  watch(idle, async (isIdle) => {
    if (isIdle && !idleRunning) {
      idleRunning = true;
      try {
        await onIdle();
      } finally {
        idleRunning = false;
      }
    }
  });
}
